<template>
  <b-container fluid>
    <b-row class="page-heading">
      <b-col>
        <h1>Nouveau transfert inter-entrepôts</h1>
        <p class="text-muted">Déplacez du stock d'un entrepôt vers un autre. Le stock sort de la source à la création et n'est intégré à la destination qu'à la réception.</p>
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="9">
        <!-- Itinéraire -->
        <div class="ibox">
          <div class="ibox-title"><i class="far fa-route"></i> Itinéraire du transfert</div>
          <div class="ibox-content">
            <b-row align-v="end">
              <b-col md="5">
                <b-form-group label="Entrepôt source *" class="mb-0">
                  <b-form-select v-model="form.source_warehouse_id" :options="warehouseOptions"></b-form-select>
                </b-form-group>
              </b-col>
              <b-col md="2" class="text-center d-none d-md-block">
                <i class="far fa-arrow-right fa-2x text-muted" style="margin-bottom: 6px"></i>
              </b-col>
              <b-col md="5">
                <b-form-group label="Entrepôt de destination *" class="mb-0">
                  <b-form-select v-model="form.target_warehouse_id" :options="targetOptions"></b-form-select>
                </b-form-group>
              </b-col>
            </b-row>
            <p v-if="sameWarehouse" class="text-danger mt-2 mb-0"><i class="far fa-exclamation-triangle"></i> La source et la destination doivent être différentes.</p>
          </div>
        </div>

        <!-- Lignes -->
        <div class="ibox">
          <div class="ibox-title">
            <i class="far fa-boxes"></i> Articles à transférer
            <span class="float-right text-muted" style="font-weight: 400; font-size: 13px">{{ form.lines.length }} ligne(s)</span>
          </div>
          <div class="ibox-content">
            <div v-for="(line, index) in form.lines" :key="line._key" class="wt-line">
              <div class="wt-line__head">
                <span class="wt-line__num">Article {{ index + 1 }}</span>
                <button
                  type="button"
                  class="wt-line__remove"
                  :disabled="form.lines.length === 1"
                  @click="removeLine(index)"
                  title="Supprimer la ligne"
                >
                  <i class="far fa-trash-alt"></i>
                </button>
              </div>

              <b-row align-v="center">
                <b-col md="4" class="wt-line__field">
                  <label>Type</label>
                  <b-form-radio-group
                    v-model="line.type"
                    buttons
                    button-variant="outline-primary"
                    size="sm"
                    class="wt-type-toggle"
                  >
                    <b-form-radio value="product"><i class="far fa-box"></i> Produit</b-form-radio>
                    <b-form-radio value="reference"><i class="far fa-barcode"></i> Référence</b-form-radio>
                  </b-form-radio-group>
                </b-col>

                <b-col :md="line.type === 'product' ? 5 : 8" class="wt-line__field">
                  <label>{{ line.type === 'product' ? 'Produit à la vente' : 'Référence unique' }}</label>
                  <!-- Vente uniquement : un matériel de location se transfère par sa
                       référence unique, pas en quantité (SFD ESM 2.11). -->
                  <product-autocomplete
                    v-if="line.type === 'product'"
                    v-model="line.product_id"
                    offer-type="selling"
                    placeholder="Rechercher un produit à la vente…"
                  ></product-autocomplete>
                  <reference-autocomplete
                    v-else
                    v-model="line.reference"
                    :warehouse-id="form.source_warehouse_id"
                    :disabled="!form.source_warehouse_id"
                  ></reference-autocomplete>
                </b-col>

                <b-col v-if="line.type === 'product'" md="3" class="wt-line__field">
                  <label>Quantité</label>
                  <b-form-input
                    type="number"
                    min="1"
                    size="sm"
                    v-model.number="line.quantity"
                    :state="form.source_warehouse_id && line.product_id && availableForLine(line) !== null ? line.quantity <= availableForLine(line) : null"
                  ></b-form-input>
                  <span
                    v-if="line.product_id && availableForLine(line) !== null"
                    :class="line.quantity > availableForLine(line) ? 'wt-stock-ko' : 'wt-stock-ok'"
                  >
                    Dispo. dans la source : {{ availableForLine(line) }}
                  </span>
                </b-col>
              </b-row>

              <p
                v-if="line.type === 'product' && !form.source_warehouse_id && line.product_id"
                class="wt-line__hint"
              >
                <i class="far fa-exclamation-triangle"></i>
                Sélectionnez l'entrepôt source pour voir le stock dispo.
              </p>
              <p v-else-if="line.type === 'reference' && !form.source_warehouse_id" class="wt-line__hint">
                <i class="far fa-exclamation-triangle"></i>
                Sélectionnez l'entrepôt source pour rechercher une référence en stock.
              </p>
              <p v-else-if="referenceMismatch(line)" class="wt-line__hint wt-line__hint--ko">
                <i class="far fa-exclamation-triangle"></i>
                {{ line.reference.internal_code }} est dans
                {{ warehouseName(line.reference.warehouse_id) || "un autre entrepôt" }} : sélectionnez-la
                de nouveau depuis l'entrepôt source.
              </p>
            </div>

            <b-button variant="white" size="sm" class="mt-2" @click="addLine">
              <i class="far fa-plus"></i> Ajouter une ligne
            </b-button>
          </div>
        </div>

        <!-- Commentaire -->
        <div class="ibox">
          <div class="ibox-content">
            <b-form-group label="Commentaire (optionnel)" class="mb-0">
              <b-form-textarea v-model="form.comment" rows="2" placeholder="Information complémentaire sur ce transfert…"></b-form-textarea>
            </b-form-group>
          </div>
        </div>
      </b-col>

      <!-- Récap / actions -->
      <b-col lg="3">
        <div class="ibox">
          <div class="ibox-title"><i class="far fa-clipboard-check"></i> Récapitulatif</div>
          <div class="ibox-content">
            <dl class="mb-3">
              <dt class="text-muted" style="font-weight: 400">Source</dt>
              <dd>{{ warehouseName(form.source_warehouse_id) || "—" }}</dd>
              <dt class="text-muted" style="font-weight: 400">Destination</dt>
              <dd>{{ warehouseName(form.target_warehouse_id) || "—" }}</dd>
              <dt class="text-muted" style="font-weight: 400">Lignes valides</dt>
              <dd>{{ validLinesCount }} / {{ form.lines.length }}</dd>
            </dl>
            <b-button variant="primary" block :disabled="saving || !canSubmit" @click="submit">
              <i class="far fa-check"></i> {{ saving ? "Enregistrement…" : "Créer le transfert" }}
            </b-button>
            <router-link :to="{ name: 'WarehouseTransferList' }" class="btn btn-white btn-block">Annuler</router-link>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<style scoped>
.wt-line {
  position: relative;
  padding: 12px 16px;
  margin-bottom: 10px;
  border: 1px solid #e7eaec;
  border-left: 3px solid #1ab394;
  border-radius: 4px;
  background: #fff;
}
.wt-line__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.wt-line__num {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #909499;
}
.wt-line__remove {
  border: none;
  background: transparent;
  color: #ed5565;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 2px 6px;
}
.wt-line__remove:hover:not(:disabled) {
  color: #d3455a;
}
.wt-line__remove:disabled {
  color: #ccc;
  cursor: not-allowed;
}
.wt-line__field label {
  display: block;
  font-size: 11px;
  text-transform: uppercase;
  color: #909499;
  margin-bottom: 4px;
}
.wt-line__hint {
  margin: 12px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: #f8ac59;
}
.wt-line__hint i {
  margin-right: 6px;
}
.wt-line__hint--ko {
  color: #ed5565;
}
/* Toggle Produit / Référence : btn-group INSPINIA pleine largeur, vert charte. */
.wt-type-toggle {
  display: flex;
}
.wt-type-toggle >>> .btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.wt-type-toggle >>> .btn i {
  margin-right: 4px;
}
/* Le `primary` bootstrap est bleu dans ce thème : on force le vert (navy INSPINIA). */
.wt-type-toggle >>> .btn-outline-primary {
  color: #1ab394;
  border-color: #1ab394;
}
.wt-type-toggle >>> .btn-outline-primary:hover,
.wt-type-toggle >>> .btn-outline-primary.active,
.wt-type-toggle >>> .btn-outline-primary:active,
.wt-type-toggle >>> .btn-outline-primary:not(:disabled):not(.disabled).active {
  color: #fff;
  background-color: #1ab394;
  border-color: #1ab394;
}
.wt-type-toggle >>> .btn-outline-primary:focus,
.wt-type-toggle >>> .btn-outline-primary.focus {
  box-shadow: 0 0 0 0.2rem rgba(26, 179, 148, 0.35);
}
.wt-stock-ok {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #909499;
}
.wt-stock-ko {
  display: block;
  margin-top: 4px;
  font-size: 13px;
  font-weight: 700;
  color: #ed5565;
}
</style>

<script>
import ProductAutocomplete from "@/components/Global/ProductAutocomplete.vue";
import ReferenceAutocomplete from "@/components/Global/ReferenceAutocomplete.vue";

export default {
  name: "WarehouseTransferForm",
  components: { ProductAutocomplete, ReferenceAutocomplete },
  data() {
    return {
      saving: false,
      lineSeq: 0,
      productStocks: {}, // cache product_id -> répartition stock par entrepôt
      form: {
        source_warehouse_id: null,
        target_warehouse_id: null,
        comment: null,
        lines: [],
      },
      typeOptions: [
        { value: "product", text: "Produit à la vente" },
        { value: "reference", text: "Référence unique" },
      ],
    };
  },
  computed: {
    warehouses() {
      return this.$store.getters["warehouse/getAll"] || [];
    },
    warehouseOptions() {
      const options = [{ value: null, text: "— Sélectionner —" }];
      this.warehouses.forEach((w) => {
        options.push({ value: w.id, text: w.internal_code + " - " + w.name });
      });
      return options;
    },
    // La destination ne peut pas être la source.
    targetOptions() {
      return this.warehouseOptions.map((o) => ({
        ...o,
        disabled: o.value !== null && o.value === this.form.source_warehouse_id,
      }));
    },
    sameWarehouse() {
      return (
        this.form.source_warehouse_id &&
        this.form.target_warehouse_id &&
        this.form.source_warehouse_id === this.form.target_warehouse_id
      );
    },
    validLinesCount() {
      return this.form.lines.filter((l) => this.isLineFilled(l)).length;
    },
    // Références sélectionnées puis rendues incohérentes par un changement d'entrepôt source.
    mismatchedReferenceLines() {
      return this.form.lines.filter((l) => this.referenceMismatch(l));
    },
    // Même référence unique sur deux lignes : elle ne peut partir qu'une fois.
    duplicatedReferenceLines() {
      const seen = {};
      return this.form.lines.filter((l) => {
        if (l.type !== "reference" || !l.reference) return false;
        const duplicate = seen[l.reference.id] === true;
        seen[l.reference.id] = true;
        return duplicate;
      });
    },
    // Lignes "produit" dont la quantité dépasse le stock dispo connu dans la source.
    overStockLines() {
      return this.form.lines.filter((l) => {
        if (l.type !== "product" || !l.product_id) return false;
        const avail = this.availableForLine(l);
        return avail !== null && l.quantity > avail;
      });
    },
    canSubmit() {
      return (
        this.form.source_warehouse_id &&
        this.form.target_warehouse_id &&
        !this.sameWarehouse &&
        this.validLinesCount > 0 &&
        this.overStockLines.length === 0 &&
        this.mismatchedReferenceLines.length === 0 &&
        this.duplicatedReferenceLines.length === 0
      );
    },
  },
  watch: {
    // Charge la répartition de stock dès qu'un produit est choisi sur une ligne.
    "form.lines": {
      deep: true,
      handler() {
        this.form.lines.forEach((l) => {
          if (l.type === "product" && l.product_id) this.ensureProductStock(l.product_id);
        });
      },
    },
  },
  mounted() {
    this.$store.dispatch("warehouse/getAll", true);
    this.$store.dispatch("product/getAllForSelect", true);
    this.prefillFromQuery();
  },
  methods: {
    // Récupère (une fois) la répartition du stock par entrepôt d'un produit.
    ensureProductStock(productId) {
      if (this.productStocks[productId] !== undefined) return;
      this.$set(this.productStocks, productId, null); // marque "en cours" pour éviter les doublons
      this.$http
        .get(process.env.BACK_API_URL + "product/" + productId + "/stocks/by-warehouse")
        .then((response) => {
          this.$set(this.productStocks, productId, response.data.distribution || []);
        })
        .catch(() => {
          this.$set(this.productStocks, productId, []);
        });
    },
    // Stock dispo du produit de la ligne dans l'entrepôt source (null = inconnu/non chargé).
    availableForLine(line) {
      if (line.type !== "product" || !line.product_id || !this.form.source_warehouse_id) return null;
      const dist = this.productStocks[line.product_id];
      if (!dist) return null;
      const row = dist.find((d) => d.warehouse_id === this.form.source_warehouse_id);
      return row ? row.available : 0;
    },
    isLineFilled(line) {
      return line.type === "product"
        ? !!line.product_id && line.quantity > 0
        : !!(line.reference && line.reference.id);
    },
    // Vrai si la référence sélectionnée n'est plus dans l'entrepôt source
    // (l'entrepôt a changé après la sélection). Une référence sans entrepôt
    // affecté reste acceptée : le back l'autorise.
    referenceMismatch(line) {
      return !!(
        line.type === "reference" &&
        line.reference &&
        line.reference.warehouse_id &&
        this.form.source_warehouse_id &&
        line.reference.warehouse_id !== this.form.source_warehouse_id
      );
    },
    // Préremplissage depuis une fiche produit / référence unique
    // (?source_warehouse_id=, ?product_id= ou ?reference_id=+?internal_code=)
    prefillFromQuery() {
      const q = this.$route.query || {};
      if (q.source_warehouse_id) {
        this.form.source_warehouse_id = parseInt(q.source_warehouse_id, 10);
      }
      if (q.product_id) {
        this.form.lines.push({
          _key: this.lineSeq++,
          type: "product",
          product_id: parseInt(q.product_id, 10),
          reference: null,
          quantity: 1,
        });
      } else if (q.reference_id) {
        // Désignation et entrepôt sont complétés par l'autocomplete au montage.
        this.form.lines.push({
          _key: this.lineSeq++,
          type: "reference",
          product_id: null,
          reference: {
            id: parseInt(q.reference_id, 10),
            internal_code: q.internal_code || "",
            product_name: null,
            warehouse_id: null,
          },
          quantity: 1,
        });
      } else {
        this.addLine();
      }
    },
    warehouseName(id) {
      const w = this.warehouses.find((w) => w.id === id);
      return w ? w.internal_code + " - " + w.name : null;
    },
    addLine() {
      this.form.lines.push({ _key: this.lineSeq++, type: "product", product_id: null, reference: null, quantity: 1 });
    },
    removeLine(index) {
      this.form.lines.splice(index, 1);
    },
    validate() {
      if (!this.form.source_warehouse_id || !this.form.target_warehouse_id) {
        this.$toastr.error("Entrepôt source et destination obligatoires.");
        return false;
      }
      if (this.sameWarehouse) {
        this.$toastr.error("La source et la destination doivent être différentes.");
        return false;
      }
      if (this.validLinesCount === 0) {
        this.$toastr.error("Ajoutez au moins une ligne valide.");
        return false;
      }
      if (this.overStockLines.length) {
        this.$toastr.error("Quantité supérieure au stock disponible dans l'entrepôt source.");
        return false;
      }
      if (this.mismatchedReferenceLines.length) {
        this.$toastr.error("Une référence sélectionnée n'est pas dans l'entrepôt source.");
        return false;
      }
      if (this.duplicatedReferenceLines.length) {
        this.$toastr.error("La même référence unique est présente sur plusieurs lignes.");
        return false;
      }
      return true;
    },
    submit() {
      if (!this.validate()) return;
      this.saving = true;

      const lines = this.form.lines
        .filter((l) => this.isLineFilled(l))
        .map((l) =>
          l.type === "product"
            ? { product_id: l.product_id, quantity: l.quantity }
            : { reference_id: l.reference.id }
        );

      this.$http
        .post(process.env.BACK_API_URL + "warehouse-transfer", {
          source_warehouse_id: this.form.source_warehouse_id,
          target_warehouse_id: this.form.target_warehouse_id,
          comment: this.form.comment,
          lines,
        })
        .then((response) => {
          this.$toastr.success("Transfert " + (response.data.transfer_number || "") + " créé.");
          this.$router.push({ name: "WarehouseTransferDetail", params: { id: response.data.id } });
        })
        .catch((error) => {
          this.saving = false;
          this.$toastr.error(
            (error.response && error.response.data && error.response.data.message) || error.message || "Échec de la création."
          );
        });
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/WarehouseTransfer/WarehouseTransferForm.vue